// activity-log.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnInit, input, signal } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { generalService } from 'src/app/services/general-info';
import { firstValueFrom } from 'rxjs';
import { ActivityLogDetailModalComponent } from './activity-log-detail-modal/activity-log-detail-modal.component';

@Component({
  selector: 'app-activity-log',
  standalone: true,
  imports: [CommonModule, CustomDatePipe, MatDialogModule],
  templateUrl: './activity-log.component.html',
  styleUrl: './activity-log.component.scss',
})
export class ActivityLogComponent implements OnInit {
  data = input<any>();
  params = input<any>();
  lead = input<any>();
  isModal = input<boolean>(false);

  logs = signal<any[]>([]);
  total = signal<number>(0);
  loading = signal<boolean>(false);
  showLoad = signal<boolean>(true);

  private paramsChild: any;

  constructor(
    private generalService: generalService,
    private datePipe: CustomDatePipe,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.data()?.data) {
      this.logs.set(this.data().data);
      this.total.set(this.data().total || 0);
    }
    
    this.paramsChild = { ...this.params() };
    this.updateShowLoad();
  }

  private updateShowLoad() {
    const pageIndex = +this.paramsChild?.pageIndex || 1;
    const pageSize = +this.paramsChild?.pageSize || 10;
    this.showLoad.set(pageIndex * pageSize < this.total());
  }

  async loadMore() {
    if (this.loading()) return;
    
    this.loading.set(true);
    this.paramsChild.pageIndex = (+this.paramsChild.pageIndex || 1) + 1;

    try {
      const response = await firstValueFrom(
        this.generalService.get(this.paramsChild, 'activityLogs')
      );
      this.logs.update(current => [...current, ...response.data]);
      this.total.set(response.total);
      this.updateShowLoad();
    } catch (error) {
      console.error('Error loading more data:', error);
    } finally {
      this.loading.set(false);
    }
  }

  openDetail(log: any) {
    this.dialog.open(ActivityLogDetailModalComponent, {
      width: '520px',
      maxHeight: '85vh',
      data: log,
      panelClass: 'activity-log-detail-modal'
    });
  }

  getEventConfig(eventType: string): { icon: string; colorClass: string; label: string } {
    if (!eventType) {
      return { icon: 'radio_button_checked', colorClass: 'color-default', label: 'Unknown Event' };
    }
    
    const config: Record<string, { icon: string; colorClass: string; label: string }> = {
      // Activity events
      activity_created: { icon: 'add_task', colorClass: 'color-activity', label: 'Activity Created' },
      activity_completed: { icon: 'task_alt', colorClass: 'color-activity-done', label: 'Activity Completed' },
      activity_updated: { icon: 'edit', colorClass: 'color-activity', label: 'Activity Updated' },
      activity_deleted: { icon: 'delete_outline', colorClass: 'color-danger', label: 'Activity Deleted' },
      
      // Message events
      sms_sent: { icon: 'sms', colorClass: 'color-message-out', label: 'SMS Sent' },
      sms_received: { icon: 'sms', colorClass: 'color-message-in', label: 'SMS Received' },
      email_sent: { icon: 'mail_outline', colorClass: 'color-message-out', label: 'Email Sent' },
      email_received: { icon: 'mark_email_unread', colorClass: 'color-message-in', label: 'Email Received' },
      
      // Lead message events (alternative naming)
      lead_incoming_message: { icon: 'move_to_inbox', colorClass: 'color-message-in', label: 'Lead Incoming Message' },
      lead_outgoing_message: { icon: 'send', colorClass: 'color-message-out', label: 'Lead Outgoing Message' },
      
      // Note events
      note_created: { icon: 'sticky_note_2', colorClass: 'color-note', label: 'Note Added' },
      note_updated: { icon: 'edit_note', colorClass: 'color-note', label: 'Note Updated' },
      lead_note_added: { icon: 'sticky_note_2', colorClass: 'color-note', label: 'Lead Note Added' },
      lead_note_updated: { icon: 'edit_note', colorClass: 'color-note', label: 'Lead Note Updated' },
      
      // Step events
      step_changed: { icon: 'swap_horiz', colorClass: 'color-step', label: 'Step Changed' },
      lead_step_changed: { icon: 'swap_horiz', colorClass: 'color-step', label: 'Lead Step Changed' },
      
      // Appointment events
      appointment_scheduled: { icon: 'event', colorClass: 'color-appointment', label: 'Appointment Scheduled' },
      appointment_completed: { icon: 'event_available', colorClass: 'color-appointment-done', label: 'Appointment Completed' },
      appointment_cancelled: { icon: 'event_busy', colorClass: 'color-danger', label: 'Appointment Cancelled' },
      appointment_rescheduled: { icon: 'update', colorClass: 'color-appointment', label: 'Appointment Rescheduled' },
      
      // Call events - updated to match backend event types
      call_incoming_completed: { icon: 'phone_callback', colorClass: 'color-call-in', label: 'Incoming Call' },
      call_outgoing_completed: { icon: 'phone_forwarded', colorClass: 'color-call-out', label: 'Outgoing Call' },
      call_missed_inbound: { icon: 'phone_missed', colorClass: 'color-danger', label: 'Missed Incoming Call' },
      call_missed_outbound: { icon: 'phone_disabled', colorClass: 'color-warning', label: 'No Answer' },
      call_failed: { icon: 'phone_locked', colorClass: 'color-danger', label: 'Call Failed' },
      
      // Lead events
      lead_created: { icon: 'person_add', colorClass: 'color-lead', label: 'Lead Created' },
      lead_updated: { icon: 'person', colorClass: 'color-lead', label: 'Lead Updated' },
      lead_won: { icon: 'emoji_events', colorClass: 'color-success', label: 'Lead Won' },
      lead_lost: { icon: 'person_off', colorClass: 'color-danger', label: 'Lead Lost' },
    };
    
    return config[eventType] || { 
      icon: 'radio_button_checked', 
      colorClass: 'color-default', 
      label: this.formatEventType(eventType) 
    };
  }

  private formatEventType(eventType: string): string {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getEventSubtitle(log: any): string {
    if (log.activity) return log.activity.title || '';
    if (log.message) return log.message.subject || this.decodeAndTruncate(log.message.content, 50);
    if (log.note) return this.decodeAndTruncate(log.note.content, 50);
    if (log.step) return `Moved to "${log.step.name}"`;
    if (log.appointment) return log.appointment.status?.name || '';
    if (log.call) {
      const duration = log.metadata?.duration;
      if (duration) {
        const mins = Math.floor(duration / 60);
        const secs = duration % 60;
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      }
      return '';
    }
    
    // Fallback: try to extract from metadata
    if (log.metadata) {
      if (log.metadata.title) return log.metadata.title;
      if (log.metadata.step_name) return `Moved to "${log.metadata.step_name}"`;
      if (log.metadata.content) return this.decodeAndTruncate(log.metadata.content, 50);
      if (log.metadata.subject) return log.metadata.subject;
    }
    
    return '';
  }

  getCreatedByName(log: any): string | null {
    if (!log.created_by) return null;
    const { name, lastName } = log.created_by;
    if (name && lastName) return `${name} ${lastName}`;
    return name || lastName || null;
  }

  getCreatedByInitials(log: any): string {
    if (!log.created_by) return '?';
    const { name, lastName } = log.created_by;
    const firstInitial = name?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial || '?';
  }

  private decodeAndTruncate(text: string | null, max: number): string {
    if (!text) return '';
    
    // Create a temporary element to decode HTML entities
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const decoded = doc.body.textContent || '';
    
    // Remove extra whitespace
    const cleaned = decoded.replace(/\s+/g, ' ').trim();
    
    return cleaned.length <= max ? cleaned : cleaned.substring(0, max) + '...';
  }
}